/**
 * Persistent rig-damage smoke and fire.
 *
 * This is deterministic geometry rather than a free-running particle emitter: every
 * parcel is reconstructed from simulation time, so replay and benchmark frames match.
 * Two crossed cards per parcel keep the plume readable across the player's full look arc
 * while the whole damage state remains one draw call.
 */

import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { ShaderStore } from '@babylonjs/core/Engines/shaderStore';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Scene } from '@babylonjs/core/scene';

import damageVert from '../../shaders/rigDamage.vert';
import damageFrag from '../../shaders/rigDamage.frag';
import { rigDamageVisualLevels } from '../rigDamageLighting';

const SMOKE_PUFFS = 10;
const FIRE_TONGUES = 4;
const CARDS_PER_PARCEL = 2;
const SITE_COUNT = 2;
const QUAD_COUNT =
  SITE_COUNT * (SMOKE_PUFFS + FIRE_TONGUES) * CARDS_PER_PARCEL;
const VERTICES_PER_QUAD = 4;

interface DamageSite {
  x: number;
  y: number;
  z: number;
  /** The second site wakes only once damage is critical. */
  critical: boolean;
  phase: number;
}

/*
 * Real named structures, not arbitrary points in open space:
 * - the open near-face gallery on the Crane Tower;
 * - the Barracks roof, visible when the player scans the port side.
 */
const DAMAGE_SITES: readonly DamageSite[] = [
  /*
   * This is the actual walking surface of the Crane Tower's third ring gallery after
   * the tower's authored 18 m drop, tucked beside its near-face service riser. The old
   * point sat between two floors against a wall, leaving the flame floating and exposing
   * the flat bottom of its card. This site is farther from the player and has a credible
   * pipe-fed source inside the tower machinery.
   */
  { x: 19.0, y: 113.3, z: 21.0, critical: false, phase: 0.4 },
  { x: -18.0, y: 109.0, z: -14.0, critical: true, phase: 2.3 },
];

let shadersRegistered = false;

function registerShaders(): void {
  if (shadersRegistered) return;
  ShaderStore.ShadersStore.deepwaterRigDamageVertexShader = damageVert;
  ShaderStore.ShadersStore.deepwaterRigDamageFragmentShader = damageFrag;
  shadersRegistered = true;
}

function fract(value: number): number {
  return value - Math.floor(value);
}

function smoothstep(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export class RigDamageEffects {
  readonly triangleCount = QUAD_COUNT * 2;

  private readonly mesh: Mesh;
  private readonly material: ShaderMaterial;
  private readonly positions = new Float32Array(QUAD_COUNT * VERTICES_PER_QUAD * 3);
  private readonly colors = new Float32Array(QUAD_COUNT * VERTICES_PER_QUAD * 4);
  private quad = 0;

  constructor(scene: Scene) {
    registerShaders();

    this.mesh = new Mesh('rigDamagePlume', scene);
    this.mesh.isPickable = false;
    this.mesh.alwaysSelectAsActiveMesh = true;
    this.mesh.renderingGroupId = 1;
    this.mesh.applyFog = false;

    const indices: number[] = [];
    const uvs: number[] = [];
    for (let quad = 0; quad < QUAD_COUNT; quad++) {
      const base = quad * VERTICES_PER_QUAD;
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
      uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    }

    const data = new VertexData();
    data.positions = Array.from(this.positions);
    data.indices = indices;
    data.uvs = uvs;
    data.colors = Array.from(this.colors);
    data.applyToMesh(this.mesh, true);

    this.material = new ShaderMaterial(
      'rigDamageMaterial',
      scene,
      { vertex: 'deepwaterRigDamage', fragment: 'deepwaterRigDamage' },
      {
        attributes: ['position', 'uv', 'color'],
        uniforms: ['worldViewProjection'],
        needAlphaBlending: true,
        needAlphaTesting: false,
      },
    );
    this.material.backFaceCulling = false;
    this.material.disableDepthWrite = true;
    this.mesh.material = this.material;
    this.mesh.setEnabled(false);
  }

  update(
    integrity: number,
    time: number,
    windDir: number,
    windSpeed: number,
    fogColor: Color3,
    cameraX: number,
    cameraZ: number,
  ): void {
    const levels = rigDamageVisualLevels(integrity);
    if (levels.smoke <= 0 && levels.fire <= 0) {
      this.mesh.setEnabled(false);
      return;
    }

    this.mesh.setEnabled(true);
    this.quad = 0;

    // Weather headings describe where the wind comes from; smoke travels away from it.
    const windX = -Math.sin(windDir);
    const windZ = -Math.cos(windDir);
    const crossX = windZ;
    const crossZ = -windX;

    for (const site of DAMAGE_SITES) {
      const siteStrength = site.critical ? levels.fire : levels.smoke;
      this.writeSmoke(site, siteStrength, time, windX, windZ, crossX, crossZ, windSpeed, fogColor);
      this.writeFire(site, siteStrength * levels.fire, time, cameraX, cameraZ);
    }

    this.mesh.updateVerticesData(VertexBuffer.PositionKind, this.positions, false, false);
    this.mesh.updateVerticesData(VertexBuffer.ColorKind, this.colors, false, false);
  }

  private writeSmoke(
    site: DamageSite,
    strength: number,
    time: number,
    windX: number,
    windZ: number,
    crossX: number,
    crossZ: number,
    windSpeed: number,
    fogColor: Color3,
  ): void {
    for (let puff = 0; puff < SMOKE_PUFFS; puff++) {
      const phase = fract(time * 0.047 + puff / SMOKE_PUFFS + site.phase);
      const appear = smoothstep(phase / 0.13);
      const disappear = 1 - smoothstep((phase - 0.50) / 0.50);
      const fade = appear * disappear;
      const drift = phase * (7 + windSpeed * 0.60);
      const wander = Math.sin(time * 0.13 + puff * 2.17 + site.phase) * (1 + phase * 3.2);
      const x = site.x + windX * drift + crossX * wander;
      const z = site.z + windZ * drift + crossZ * wander;
      const y = site.y + 2.5 + phase * 38;
      const width = 3.0 + phase * 10.0;
      const height = 4.0 + phase * 12.0;
      const haze = phase * 0.58;
      const r = lerp(0.065, 0.045 + fogColor.r * 0.30, haze);
      const g = lerp(0.058, 0.041 + fogColor.g * 0.30, haze);
      const b = lerp(0.052, 0.038 + fogColor.b * 0.32, haze);
      const alpha = strength * fade * (0.62 - phase * 0.20);
      const shear = phase * 2.2;

      this.writeCrossedCards(x, y, z, width, height, shear * windX, shear * windZ, r, g, b, alpha);
    }
  }

  private writeFire(
    site: DamageSite,
    strength: number,
    time: number,
    cameraX: number,
    cameraZ: number,
  ): void {
    const viewX = cameraX - site.x;
    const viewZ = cameraZ - site.z;
    const viewLength = Math.max(0.001, Math.hypot(viewX, viewZ));
    const rightX = viewZ / viewLength;
    const rightZ = -viewX / viewLength;

    for (let tongue = 0; tongue < FIRE_TONGUES; tongue++) {
      const flicker = 0.5 + 0.5 * Math.sin(time * (4.1 + tongue * 0.37) + tongue * 2.3 + site.phase);
      const spreadX = Math.sin(tongue * 4.17 + site.phase) * 0.34;
      const spreadZ = Math.cos(tongue * 3.31 + site.phase) * 0.30;
      const width = (0.38 + tongue * 0.045) * (0.90 + flicker * 0.18);
      const height = (0.62 + flicker * 0.62) * (0.78 + strength * 0.22);
      const r = lerp(1.55, 2.25, 1 - tongue / FIRE_TONGUES);
      const g = 0.31 + flicker * 0.34;
      const b = 0.02 + flicker * 0.03;
      const alpha = strength * (0.32 + flicker * 0.15);

      const x = site.x + spreadX;
      const y = site.y + height * 0.46;
      const z = site.z + spreadZ;
      const shearX = Math.sin(time * 1.7 + tongue) * 0.18;
      const shearZ = Math.cos(time * 1.4 + tongue) * 0.16;
      this.writeCard(
        x,
        y,
        z,
        width,
        height,
        shearX,
        shearZ,
        rightX,
        rightZ,
        r,
        g,
        b,
        alpha,
      );
      // A shallow second card gives the flame volume without presenting an edge-on strip.
      this.writeCard(
        x,
        y,
        z,
        width * 0.88,
        height * 0.92,
        shearX,
        shearZ,
        rightX * 0.82 - rightZ * 0.57,
        rightZ * 0.82 + rightX * 0.57,
        r,
        g,
        b,
        alpha * 0.62,
      );
    }
  }

  private writeCrossedCards(
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    shearX: number,
    shearZ: number,
    r: number,
    g: number,
    b: number,
    alpha: number,
  ): void {
    this.writeCard(x, y, z, width, height, shearX, shearZ, 1, 0, r, g, b, alpha);
    this.writeCard(x, y, z, width, height, shearX, shearZ, 0, 1, r, g, b, alpha);
  }

  private writeCard(
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    shearX: number,
    shearZ: number,
    rightX: number,
    rightZ: number,
    r: number,
    g: number,
    b: number,
    alpha: number,
  ): void {
    const vertex = this.quad * VERTICES_PER_QUAD;
    const halfWidth = width * 0.5;
    const halfHeight = height * 0.5;
    const bottomX = x - shearX * 0.5;
    const bottomZ = z - shearZ * 0.5;
    const topX = x + shearX * 0.5;
    const topZ = z + shearZ * 0.5;

    const corners = [
      [bottomX - rightX * halfWidth, y - halfHeight, bottomZ - rightZ * halfWidth],
      [bottomX + rightX * halfWidth, y - halfHeight, bottomZ + rightZ * halfWidth],
      [topX + rightX * halfWidth, y + halfHeight, topZ + rightZ * halfWidth],
      [topX - rightX * halfWidth, y + halfHeight, topZ - rightZ * halfWidth],
    ] as const;

    for (let corner = 0; corner < VERTICES_PER_QUAD; corner++) {
      const positionIndex = (vertex + corner) * 3;
      const colorIndex = (vertex + corner) * 4;
      const p = corners[corner] as readonly [number, number, number];
      this.positions[positionIndex] = p[0];
      this.positions[positionIndex + 1] = p[1];
      this.positions[positionIndex + 2] = p[2];
      this.colors[colorIndex] = r;
      this.colors[colorIndex + 1] = g;
      this.colors[colorIndex + 2] = b;
      this.colors[colorIndex + 3] = alpha;
    }
    this.quad++;
  }

  dispose(): void {
    this.material.dispose();
    this.mesh.dispose();
  }
}
